import { createPart, themeQuartz } from "ag-grid-enterprise";

// ─── ElectionBuddy AG-Grid theme ───────────────────────────────────────────
// Design tokens via withParams — replaces all --ag-* CSS variable declarations.
// Structural overrides via createPart/withPart — replaces scoped CSS selectors.
// Composable for dark mode: ebTheme.withParams({ headerBackgroundColor: "..." })
export const ebTheme = themeQuartz
  .withParams({
    // Header
    headerBackgroundColor: "#262D34",
    headerTextColor: "#ffffff",
    headerColumnResizeHandleColor: "#606060",
    headerHeight: 56,
    columnBorder: { style: "solid", color: "rgba(255,255,255,0.25)" },
    // Typography
    fontSize: 14,
    // Rows
    oddRowBackgroundColor: "#f0f4f8",
    rowHoverColor: "#d0e4f0",
    selectedRowBackgroundColor: "#dce6f0",
    rowBorder: { style: "solid", color: "#e5e7eb" },
    // Selection / checkboxes
    checkboxCheckedBorderColor: "#172837",
    checkboxCheckedBackgroundColor: "#172837",
    checkboxUncheckedBorderColor: "#d1d5db",
    checkboxUncheckedBackgroundColor: "#ffffff",
    // Inputs / focus
    inputBorder: { style: "solid", color: "#d1d5db" },
    inputFocusBorder: { style: "solid", color: "#1d4ed8" },
    // Column drop zone (group-by bar)
    columnDropCellBackgroundColor: "#262D34",
    columnDropCellTextColor: "#ffffff",
  })
  .withPart(createPart({
    css: `
      /* Header: uppercase bold labels */
      .ag-header-cell-label, .ag-header-group-cell-label {
        font-weight: 700;
        text-transform: uppercase;
        font-size: 1.5rem;
        letter-spacing: 0.05em;
      }
      /* Force header text color on all header children (icons, sort arrows, etc.) */
      .ag-header *, .ag-header-cell *, .ag-header-group-cell * {
        color: var(--ag-header-text-color) !important;
      }
      /* Sort indicator icon is display:none (zero width) until sorted, so its
         column would otherwise narrow and wrap the header text. Scoped to
         ag-header-cell-sorted-* (AG Grid's own sort-state class) so only the
         column actually being sorted reserves the space, not every column. */
      .ag-header-cell-sorted-asc .ag-sort-indicator-container,
      .ag-header-cell-sorted-desc .ag-sort-indicator-container,
      .ag-header-cell-sorted-mixed .ag-sort-indicator-container {
        min-width: calc(var(--ag-icon-size, 16px) + var(--ag-spacing, 4px));
      }
      .ag-header-row > .ag-header-cell:last-of-type,
      .ag-header-row > .ag-header-group-cell:last-of-type,
      .ag-row > .ag-cell:last-of-type {
        border-right: none !important;
      }
      .ag-header-row > .ag-header-cell:last-of-type::after,
      .ag-header-row > .ag-header-group-cell:last-of-type::after {
        display: none !important;
      }
      /* Panel title bar (Choose Columns dialog) */
      .ag-panel-title-bar { background-color: var(--ag-header-background-color) !important; }
      .ag-panel-title-bar *, .ag-column-select-header, .ag-column-select-header * {
        color: var(--ag-header-text-color) !important;
      }
      /* Group-by drop zone bar */
      .ag-column-drop-horizontal { background-color: var(--ag-header-background-color) !important; }
      .ag-column-drop-horizontal *, .ag-column-drop-empty-message { color: var(--ag-header-text-color) !important; }
      /* Cell: flex-align so single-line cells sit flush with multi-line status cells */
      .ag-cell { display: flex; align-items: center; overflow: hidden; }
      /* Status cell renderer layout */
      .ag-cell .ag-status-cell {
        display: flex; flex-direction: column; line-height: 1.3;
        gap: 1px; padding: 6px 0; min-width: 0; overflow: hidden;
      }
      /* Pagination panel: left-aligned */
      .ag-paging-panel { justify-content: flex-start; }
      /* Sidebar: hide right-rail tab strip (buttons are used instead) */
      .ag-side-buttons { display: none !important; }
      /* Floating filter row */
      .ag-floating-filter { background-color: #f9fafb; }
      /* Filter active indicator */
      .ag-header-cell-filter-button.ag-filter-active {
        --ag-background-color: rgba(255, 128, 2, 0.75) !important;
        background: rgba(255, 128, 2, 0.9) !important;
        background-color: rgba(255, 128, 2, 0.9) !important;
        border-radius: 3px;
        padding: 2px 4px;
        box-shadow: none !important;
        border: none !important;
        outline: none !important;
        position: relative;
      }
      .ag-header-cell-filter-button.ag-filter-active .ag-icon-filter { color: #ffffff !important; }
      .ag-header-cell-filter-button.ag-filter-active::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 7px;
        height: 7px;
        background-color: #FFAB57E6;
        border-radius: 50%;
        transform: translate(2px, -2px);
      }
      /* Remove blue border AG-Grid puts on header cell when column is filtered */
      .ag-header-cell:has(.ag-filter-active) {
        border-color: rgba(255,255,255,0.25) !important;
        outline: none !important;
        box-shadow: none !important;
      }
      .ag-floating-filter-input input,
      .ag-floating-filter .ag-text-field-input,
      .ag-floating-filter .ag-input-field-input {
        height: 24px; min-height: 24px; padding: 0 6px; font-size: 1.2rem;
        background-color: #ffffff; color: #172837 !important;
        border: 1px solid #d1d5db; border-radius: 3px; width: 100%;
      }
      .ag-floating-filter-button-button, .ag-floating-filter-button-button .ag-icon { color: #1d4ed8 !important; }
      /* Columns tool panel */
      .ag-column-select { background-color: #ffffff; color: #172837; }
      .ag-column-select-header {
        background-color: #ffffff; border-bottom: 1px solid #e5e7eb;
        padding: 6px 8px; color: #172837 !important;
      }
      .ag-column-select-header * { color: #172837 !important; }
      .ag-column-select .ag-virtual-list-item { background-color: #ffffff; }
      .ag-column-select-column, .ag-column-select-column-group { background-color: #ffffff; color: #172837; }
      .ag-column-select-column:hover, .ag-column-select-column-group:hover { background-color: #eff6ff; }
      .ag-column-select-column-label, .ag-column-select-column-group-label { font-size: 1.2rem; }
      /* Sidebar panel body */
      .ag-tool-panel-wrapper, .ag-filter-toolpanel { background-color: #ffffff; color: #172837; }
      /* Tool panel title bar — mounted by ag_grid_base_controller because the tab strip that
         would otherwise close a panel is hidden above. Stacking it over the panel body means
         the body can no longer claim the wrapper's full height. */
      .ag-tool-panel-wrapper { display: flex; flex-direction: column; }
      .ag-tool-panel-wrapper > .ag-tool-panel-content { height: auto; flex: 1 1 auto; min-height: 0; }
      .ag-grid-tool-panel-header {
        display: flex; align-items: center; justify-content: space-between; gap: 8px;
        flex: 0 0 auto; padding: 6px 8px;
        background-color: #f0f4f8; border-bottom: 1px solid #e5e7eb;
      }
      .ag-grid-tool-panel-header__title {
        font-size: 1.2rem; font-weight: 600; letter-spacing: 0.04em;
        text-transform: uppercase; color: #172837;
      }
      .ag-grid-tool-panel-header__clear-all {
        margin-left: auto; flex: 0 0 auto;
        padding: 2px 6px; background: transparent; border: none; border-radius: 3px;
        font-size: 1.1rem; font-weight: 600; color: #1d4ed8; cursor: pointer;
      }
      .ag-grid-tool-panel-header__clear-all:hover:not(:disabled) { background-color: #dce6f0; }
      .ag-grid-tool-panel-header__clear-all:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 1px; }
      .ag-grid-tool-panel-header__clear-all:disabled { color: #9ca3af; cursor: default; }
      .ag-grid-tool-panel-header__close {
        display: inline-flex; align-items: center; justify-content: center;
        width: 20px; height: 20px; padding: 0; flex: 0 0 auto;
        background: transparent; border: none; border-radius: 3px;
        color: #172837; cursor: pointer;
      }
      .ag-grid-tool-panel-header__close:hover { background-color: #dce6f0; }
      .ag-grid-tool-panel-header__close:focus-visible { outline: 2px solid #1d4ed8; outline-offset: 1px; }
      /* New filter panel (agNewFiltersToolPanel) */
      .ag-filter-panel-container { background-color: #ffffff; padding: 4px; }
      .ag-filter-card { background-color: #ffffff; }
      .ag-filter-card-add { padding: 4px 0; }
      .ag-filter-add-select .ag-picker-field-wrapper {
        height: 26px; min-height: 26px; background-color: #f9fafb; color: #172837;
        border: 1px solid #d1d5db; border-radius: 3px; padding: 0 8px;
        font-size: 1.2rem;
      }
      .ag-filter-add-select .ag-picker-field-wrapper *,
      .ag-filter-add-select .ag-picker-field-display,
      .ag-filter-add-select .ag-label { font-size: 1.2rem !important; color: #9ca3af; }
      .ag-filter-add-select .ag-picker-field-wrapper:focus-within { border-color: #1d4ed8; }
      .ag-filter-toolpanel-header, .ag-filter-toolpanel-group-title-bar {
        background-color: #f0f4f8 !important; color: #172837 !important;
      }
      .ag-filter-toolpanel-header *, .ag-filter-toolpanel-group-title-bar * { color: #172837 !important; }
      .ag-filter-toolpanel .ag-icon, .ag-filter-toolpanel .ag-group-title-bar-icon { color: #1d4ed8; }
      .ag-filter-toolpanel-group-item { color: #172837; }
      .ag-filter-toolpanel-group-item:hover { background-color: #eff6ff; }
      /* Set filter popup (portalled to <body> — outside component scope).
       * Note: withPart CSS is already scoped to the theme, so no prefix needed. */
      .ag-filter {
        background-color: #ffffff; color: #172837;
        border: 1px solid #e5e7eb;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      .ag-filter .ag-set-filter-list { background-color: #ffffff; }
      .ag-filter .ag-virtual-list-item,
      .ag-filter .ag-set-filter-item { color: #172837; }
      .ag-filter .ag-virtual-list-item:hover { background-color: #eff6ff; }
      .ag-filter .ag-set-filter-item-value { color: #172837; }
      /* Panel search inputs — left padding accounts for the icon preceding the input */
      .ag-mini-filter .ag-input-field-input,
      .ag-column-select-header .ag-text-field-input,
      .ag-filter-toolpanel-search .ag-text-field-input {
        height: 26px; min-height: 26px; padding: 0 8px 0 26px; font-size: 1.2rem;
        background-color: #f9fafb; color: #172837;
        border: 1px solid #d1d5db; border-radius: 3px;
      }
      .ag-mini-filter .ag-input-field-input:focus,
      .ag-column-select-header .ag-text-field-input:focus,
      .ag-filter-toolpanel-search .ag-text-field-input:focus { border-color: #1d4ed8; }
      /* Text filter popup — value input and condition-type dropdown */
      .ag-filter .ag-text-field-input-wrapper .ag-text-field-input {
        height: 26px; min-height: 26px; padding: 0 8px 0 26px; font-size: 1.2rem;
        background-color: #f9fafb; color: #172837;
        border: 1px solid #d1d5db; border-radius: 3px; width: 100%;
      }
      .ag-filter .ag-text-field-input-wrapper .ag-text-field-input:focus {
        border-color: #1d4ed8; outline: none;
      }
.ag-text-filter .ag-picker-field-wrapper {
        height: 26px; min-height: 26px; background-color: #f9fafb; color: #172837;
        border: 1px solid #d1d5db; border-radius: 3px; padding: 0 8px;
        font-size: 1.2rem;
      }
      .ag-text-filter .ag-picker-field-wrapper:focus-within { border-color: #1d4ed8; }
      .ag-text-filter .ag-picker-field-display,
      .ag-text-filter .ag-picker-field-wrapper * { font-size: 1.2rem !important; color: #172837; }
      /* Number filter popup — value input, same compact sizing as the text filter input above */
      .ag-filter .ag-number-field-input {
        height: 26px; min-height: 26px; padding: 0 8px 0 26px; font-size: 1.2rem;
        background-color: #f9fafb; color: #172837;
        border: 1px solid #d1d5db; border-radius: 3px; width: 100%;
      }
      .ag-filter .ag-number-field-input:focus {
        border-color: #1d4ed8; outline: none;
      }
      /* Filter popup footer: de-emphasized into a small text-link — AG-Grid's
         default bordered/uppercase button is too heavy for a popup footer. */
      .ag-filter-apply-panel {
        padding: 4px 8px;
        border-top: 1px solid #e5e7eb;
        gap: 4px;
      }
      .ag-filter-apply-panel-button {
        background-color: transparent;
        border: none;
        box-shadow: none;
        padding: 2px 6px;
        height: auto;
        min-height: 0;
        margin-left: 0;
        font-size: 1.1rem;
        font-weight: 500;
        text-transform: none;
        color: #172837;
        border-radius: 3px;
      }
      .ag-filter-apply-panel-button:hover {
        background-color: #eff6ff;
      }
      .ag-filter-apply-panel-button:active {
        background-color: #dbeafe;
      }
      /* Date filter: suppress the magnifying-glass ::before that the Quartz theme
         injects into all .ag-filter-filter input wrappers — it overlaps the
         browser's native date-picker calendar icon. */
      .ag-date-filter .ag-input-wrapper::before {
        display: none !important;
      }
      /* Pinned bottom (Totals) row: column separators */
      .ag-floating-bottom .ag-cell {
        border-right: 1px solid #d1d5db;
        font-weight: 600;
      }
      .ag-checkbox-input-wrapper:has(input:disabled) {
        background-color: #D6D6D6 !important;
        opacity: 1 !important;
        cursor: not-allowed !important;
      }
    `,
  }));

// Smaller header labels for grids with cramped title space (e.g. Admin Users);
// matches ebEditableTheme's own header-shrink target below.
export const ebCompactHeaderTheme = ebTheme
  .withPart(createPart({
    css: `
      .ag-header-cell-label, .ag-header-group-cell-label {
        font-size: 1.15rem;
      }
    `,
  }));

// Plain-cased header variant: the shared theme shouts its headers (700 weight, uppercase,
// letter-spaced), which a customer read as harder to scan than the sentence-cased labels the
// rest of the app uses. Deliberately a variant rather than a change to ebTheme itself — every
// admin grid renders through that theme, and this was asked for on one page.
export const ebPlainHeaderTheme = ebTheme
  .withPart(createPart({
    css: `
      .ag-header-cell-label, .ag-header-group-cell-label {
        font-weight: 600;
        text-transform: none;
        letter-spacing: normal;
      }
    `,
  }));

// Light header variant for editable grids (baby blue, matches legacy Handsontable
// header — see handsontable_component.scss). Also tightens row height + fixes
// cell editor sizing (see rowHeight/withPart below); checkbox styles unchanged.
export const ebEditableTheme = ebTheme
  .withParams({
    headerBackgroundColor: "#cde0ef",
    headerTextColor: "#000000",
    columnBorder: { style: "solid", color: "rgba(0,0,0,0.15)" },
    headerHeight: 32,
    // Tighter rows for editable grids (AG-Grid's fontSize-14 default computes to 42px).
    rowHeight: 30,
    // Resize-handle separator height is a % of headerHeight (default 30%), so shrinking
    // the header shrank it too — bump the percentage back up to keep it visible.
    headerColumnResizeHandleHeight: "40%",
  })
  .withPart(createPart({
    // Shrink header label font size + soften weight for editable grids (overrides ebTheme's shared 1.5rem/700).
    css: `
      .ag-header-cell-label, .ag-header-group-cell-label {
        font-size: 1.15rem;
        font-weight: 500;
      }
      /* Cell editor inputs ignore the grid's fontSize param and use AG-Grid's raw
         default (18px), rendering oversized against this theme's 14px cell text.
         Pin to the same font-size var + fill the row height across all three
         editable grids (editable, growable, provision) that share this theme. */
      .ag-cell-editor .ag-input-field-input,
      .ag-cell-editor .ag-text-field-input {
        font-size: var(--ag-font-size);
        height: 100%;
        min-height: 100%;
      }
      /* .ag-cell's flex only centers vertically, so justify-content centers horizontally. */
      .ag-row-number-header, .ag-row-number-cell {
        font-size: 1.15rem;
        font-weight: 450;
        justify-content: center;
        text-align: center;
      }
    `,
  }));
